// src/pages/Exams/generate/HistoryTab.tsx
import { Link } from 'react-router-dom';
import { useJobHistory, HISTORY_LIMIT } from './hooks/useJobHistory';
import type { JobHistoryRow } from './types';
import { Badge, PagedFooter, PanelCard, EmptyState, ErrorPanel, Spinner, SortableTh, Table, Td, Th } from '../../../school_panel/components/ui';
import { FaFileAlt } from '../../../school_panel/components/ui/icons';
import { useSortableTable, SortColumn } from '../../../school_panel/lib/useSortableTable';
import { usePagination } from '../../../school_panel/lib/usePagination';
import { gradeCompact } from '../../../school_panel/lib/gradeLabel';
import { timeOf } from '../../../school_panel/lib/dates';

const ACTION_LINK = 'inline-flex min-h-[34px] items-center rounded-[10px] border border-line-strong bg-surface px-[11px] text-xs font-semibold text-ink hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';

type Tone = 'primary' | 'success' | 'warning' | 'danger' | 'neutral' | 'info' | 'accent';

// Design pills: Needs review = p-acc, Saved = p-ok, Failed = p-crit, the rest p-info.
const PHASE_TONE: Record<string, Tone> = {
  saved: 'success',
  review_pending: 'accent',
  failed: 'danger',
  cancelled: 'info',
};

/** "12 Oct 09:02" (no year). */
function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${timeOf(d)}`;
}

const PHASE_LABEL: Record<string, string> = {
  queued: 'Queued',
  fetching_chunks: 'Generating',
  generating: 'Generating',
  scoring: 'Generating',
  review_pending: 'Needs review',
  saved: 'Saved',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const IN_FLIGHT = ['queued', 'fetching_chunks', 'generating', 'scoring'];

const COLUMNS: ReadonlyArray<SortColumn<JobHistoryRow>> = [
  { key: 'name',     accessor: (j) => j.name || `#${j.id}` },
  { key: 'status',   accessor: (j) => j.phase },
  { key: 'progress', accessor: (j) => j.progress_completed, type: 'number' },
  { key: 'created',  accessor: (j) => j.created_at, type: 'date' },
];

export function HistoryTab() {
  const { data, isLoading, error } = useJobHistory();
  const { rows, sort, toggle } = useSortableTable<JobHistoryRow>(
    data, COLUMNS, { key: 'created', dir: 'desc' });
  const pg = usePagination<JobHistoryRow>(rows, 10, `${sort?.key}:${sort?.dir}`);

  if (isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (error) return <ErrorPanel title="Could not load history" body="Try refreshing the page." />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<FaFileAlt />}
        title="No generation runs yet"
        body="Exams you generate will show up here."
      />
    );
  }

  return (
    <PanelCard title="Generation history" subtitle="Your recent generation runs, newest first.">
    <Table minWidth={680}>
        <thead>
          <tr className="text-left">
            <SortableTh columnKey="name" sort={sort} onToggle={toggle} className="border-b border-line">Name</SortableTh>
            <SortableTh columnKey="created" sort={sort} onToggle={toggle} className="border-b border-line">Created</SortableTh>
            <SortableTh columnKey="progress" sort={sort} onToggle={toggle} align="right" className="border-b border-line">Questions</SortableTh>
            <SortableTh columnKey="status" sort={sort} onToggle={toggle} className="border-b border-line">Status</SortableTh>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {pg.pageItems.map((j: JobHistoryRow) => {
            const inFlight = IN_FLIGHT.includes(j.phase);
            const resumable = inFlight || j.phase === 'review_pending';
            const metaParts = [
              j.grade_name ? gradeCompact(j.grade_name) : '',
              j.subject_name || '',
              `${j.num_forms} ${j.num_forms === 1 ? 'form' : 'forms'}`,
            ].filter(Boolean);
            const metaLine = metaParts.join(' · ');
            return (
              <tr key={j.id}>
                <Td>
                  <div className="max-w-[24rem] break-words font-semibold text-ink">{j.name || `#${j.id}`}</div>
                  {metaLine && <div className="text-xs text-muted">{metaLine}</div>}
                </Td>
                <Td className="whitespace-nowrap font-mono text-[13px] text-muted">{whenLabel(j.created_at)}</Td>
                <Td align="right" className="font-mono tabular-nums text-ink">
                  {j.total_questions != null ? j.total_questions : `${j.progress_completed}/${j.progress_total}`}
                </Td>
                <Td>
                  <Badge tone={PHASE_TONE[j.phase] ?? (inFlight ? 'info' : 'neutral')}>
                    {PHASE_LABEL[j.phase] ?? j.phase.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())}
                  </Badge>
                </Td>
                <Td>
                  <div className="flex flex-wrap justify-end gap-2">
                    {resumable
                      ? <Link to={`/exams/generate/job/${j.id}`} className={ACTION_LINK}>
                          {j.phase === 'review_pending' ? 'Open review' : 'Resume'}
                        </Link>
                      : null}
                    {j.exam_set != null && (
                      <Link to={`/exams/exam-set/${j.exam_set}`} className={ACTION_LINK}>
                        Open exam set
                      </Link>
                    )}
                    {j.phase === 'failed' && (
                      <Link to="/exams/generate" className={ACTION_LINK}>Start over</Link>
                    )}
                    {!resumable && j.exam_set == null && j.phase !== 'failed' && <span className="text-muted">—</span>}
                  </div>
                </Td>
              </tr>
            );
          })}
        </tbody>
    </Table>
    {data && data.length >= HISTORY_LIMIT && (
      <p className="mt-3 text-xs text-muted">Showing your {HISTORY_LIMIT} most recent runs.</p>
    )}
    <PagedFooter className="mt-4" from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Generation history pagination" />
    </PanelCard>
  );
}
